import { useState } from "react";
import type { Stage } from "@salesboard/contracts";

const STORAGE_PREFIX = "board:collapsed:";

/** Exported so the test names the same key the hook writes, rather than a copy of it. */
export function collapsedStorageKey(stageId: string): string {
  return STORAGE_PREFIX + stageId;
}

/**
 * Whether a stage row starts collapsed: the closed rows do, the open ones do not.
 *
 * Keyed off `closedType` rather than the stage's name, because stages are
 * admin-configurable — a second "Closed won" stage, or one renamed in Czech, must still
 * open with its archive rows shut.
 */
export function defaultCollapsed(stage: Stage): boolean {
  return stage.closedType !== "open";
}

function readDeviation(stageId: string): boolean | undefined {
  try {
    const raw = localStorage.getItem(collapsedStorageKey(stageId));
    if (raw === "true") return true;
    if (raw === "false") return false;
    return undefined;
  } catch {
    // Private-mode Safari throws on access rather than returning null. A display
    // preference is not worth a blank board, so the design's default stands in.
    return undefined;
  }
}

function writeDeviation(stage: Stage, collapsed: boolean): void {
  try {
    if (collapsed === defaultCollapsed(stage)) localStorage.removeItem(collapsedStorageKey(stage.id));
    else localStorage.setItem(collapsedStorageKey(stage.id), String(collapsed));
  } catch {
    // The row still collapses for this session; only the memory of it is lost.
  }
}

/**
 * Which stage rows are collapsed — `localStorage`, per stage id, **deviations only**.
 *
 * This is the second piece of state in the product that is not in the URL, and it is here
 * for `perPage`'s reason: it is a display preference rather than part of the view, so a
 * shared link must open with the *designed* defaults and not with the sharer's collapse
 * habits. Storing deviations rather than absolute values is what makes a newly created
 * closed stage born collapsed for everyone, including readers whose browser has no entry
 * for it.
 *
 * Storage is read during render rather than copied into state up front, because the stage
 * list arrives with the board response — long after this hook first runs — and a value
 * seeded from an empty list would have to be re-seeded by an effect, which is one paint of
 * every archive row expanded.
 */
export function useCollapsedStages(): {
  isCollapsed: (stage: Stage) => boolean;
  toggle: (stage: Stage) => void;
} {
  // Only the deviations changed *in this session*. Its purpose is re-rendering; storage is
  // the source of truth for everything else.
  const [session, setSession] = useState<Record<string, boolean>>({});

  const isCollapsed = (stage: Stage): boolean =>
    session[stage.id] ?? readDeviation(stage.id) ?? defaultCollapsed(stage);

  const toggle = (stage: Stage): void => {
    const next = !isCollapsed(stage);
    writeDeviation(stage, next);
    setSession((previous) => ({ ...previous, [stage.id]: next }));
  };

  return { isCollapsed, toggle };
}
